import { startTransition } from 'react'

import { yieldToMain } from './yieldToMain'

/**
 * Feature-gated INP scheduling. When the gate is OFF (the default) every helper runs its work
 * SYNCHRONOUSLY — the pre-INP behaviour — so the deferral can be dark-shipped and rolled back
 * instantly. The gate is client-side and stable within a session, so it is read once (pushed in via
 * `setInteractionSchedulingEnabled` from a provider-level effect) and cached in memory here; the hot
 * helpers never touch Statsig.
 */
let interactionSchedulingEnabled = false

export function setInteractionSchedulingEnabled(enabled: boolean): void {
  interactionSchedulingEnabled = enabled
}

export function isInteractionSchedulingEnabled(): boolean {
  return interactionSchedulingEnabled
}

export function runAfterInteractionPaint(work: () => void | Promise<void>): void {
  if (!interactionSchedulingEnabled) {
    void work()
    return
  }

  void (async () => {
    await yieldToMain()
    await work()
  })()
}

/**
 * Schedules `work` for the next idle period (falling back to a post-paint task where
 * `requestIdleCallback` is unavailable). Returns a cancel function so callers can abort the
 * pending work on unmount — e.g. deferred analytics that would otherwise fire after teardown.
 */
export function runWhenMainThreadIdle(work: () => void | Promise<void>, timeout = 2000): () => void {
  if (!interactionSchedulingEnabled) {
    void work()
    return () => undefined
  }

  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    const handle = window.requestIdleCallback(
      () => {
        void work()
      },
      { timeout },
    )
    return () => {
      window.cancelIdleCallback(handle)
    }
  }

  runAfterInteractionPaint(work)
  return () => undefined
}

export function runInInteractionTransition(work: () => void): void {
  if (!interactionSchedulingEnabled) {
    work()
    return
  }

  startTransition(work)
}
